import React, { forwardRef, useCallback, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import CreditCardIcon from '@atlaskit/icon/core/credit-card';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import type { NewIconProps } from '@atlaskit/icon/types';
import Link from '@atlaskit/link';
import {
	TopNavButton,
	TopNavIconButton,
	TopNavLinkButton,
} from '@atlaskit/navigation-system/experimental/top-nav-button';
import { useHasCustomTheme } from '@atlaskit/navigation-system/experimental/use-has-custom-theme';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	Anchor,
	type AnchorProps,
	Box,
	Flex,
	Pressable,
	type PressableProps,
	Stack,
	Text,
	xcss,
	// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
} from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	type ACTEvent,
	AdControlDropdownMenu,
	AdControlProvider,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';
import { type TouchpointIntent } from '@atlassian/touchpoint-commons-event-analytics';

import { type LinkId } from '../analytics/types';
import { UiViewedEvent } from '../analytics/ui-viewed-event';
import { useAnalytics } from '../analytics/use-analytics';
import { useOnClickDismissMenu } from '../analytics/use-on-click-dismiss-menu';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { getTrialOfferingName } from '../services/get-trial-offering-name';
import { isEligibleForEAtoOSBExperiment } from '../services/is-eligible-for-ea-to-osb-experiment';
import { type ButtonContent, useButtonContent } from '../services/use-button-content';
import { useTracedUrl } from '../services/use-traced-url';

import { useSpotlight } from './spotlights';

export const EDITION_AWARENESS_SPOTLIGHT_TARGET = 'edition-awareness';

const buttonStyles = xcss({
	borderWidth: 'border.width',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	borderStyle: 'solid',
	backgroundColor: 'elevation.surface',
	borderRadius: 'radius.small',
	paddingInline: 'space.150',
	paddingBlock: 'space.050',
	minHeight: '32px',
	color: 'color.text',
	transitionProperty: 'color, background-color',
	transitionDuration: '200ms',
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	textDecoration: 'none',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		textDecoration: 'none',
		color: 'color.text',
	},

	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
		textDecoration: 'none',
	},
});

const defaultStyles = xcss({
	color: 'color.text.discovery',
	borderColor: 'color.border.discovery',

	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
		color: 'color.text.discovery',
	},

	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
		color: 'color.text.discovery',
	},
});

const warningStyles = xcss({
	borderColor: 'color.border.warning',
});

const dangerStyles = xcss({
	borderColor: 'color.border.danger',
});

const iconStyles = xcss({
	display: 'flex',
	// icon size cannot grow and shrink
	flexGrow: 0,
	flexShrink: 0,
	alignSelf: 'center',
	userSelect: 'none',
});

const dismissibleStyles = xcss({
	borderTopRightRadius: '0',
	borderBottomRightRadius: '0',
});

const dismissMenuStyles = xcss({
	borderWidth: 'border.width',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	borderStyle: 'solid',
	backgroundColor: 'elevation.surface',
	borderRadius: 'radius.small',
	paddingInline: 'space.100',
	paddingBlock: '0',
	minHeight: '32px',
	color: 'color.text',
	transitionProperty: 'color, background-color',
	transitionDuration: '200ms',
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	textDecoration: 'none',
	borderColor: 'color.border.discovery',
	borderLeftWidth: '0',
	borderTopLeftRadius: '0',
	borderBottomLeftRadius: '0',

	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
	},

	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
	},
});

const reasonsUlStyles = xcss({
	marginTop: '0',
	marginLeft: 'space.200',
	paddingLeft: 'space.100',
});

function Icon({
	appearance,
	icon,
	color,
}: {
	appearance?: ButtonContent['appearance'];
	/** Used in the top nav when a customer has a custom theme applied */
	color?: NewIconProps['color'];
	icon: ButtonContent['icon'];
}) {
	if (icon === 'warning') {
		const defaultColor =
			appearance === 'danger' ? token('color.icon.danger') : token('color.icon.warning');

		return <StatusWarningIcon label="" color={color ?? defaultColor} size="small" />;
	}

	if (icon === 'credit-card') {
		return (
			<CreditCardIcon label="" color={color ?? token('color.icon.discovery')} LEGACY_size="small" />
		);
	}

	return <EditionsIcon label="" color={color ?? token('color.icon.discovery')} />;
}

export const EditionAwarenessButton: React.ForwardRefExoticComponent<
	Omit<Omit<PressableProps, 'children'>, 'ref'> & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, Omit<PressableProps, 'children'>>(function (
	{ onClick, ...rest },
	ref,
) {
	const buttonContent = useButtonContent();

	if (buttonContent === null) {
		throw new Error('The button content is null, please check the context');
	}

	const { text, icon, appearance = 'default', isDismissible, isEducationDiscount } = buttonContent;

	const hasCustomTheme = useHasCustomTheme();

	const { sendAnalyticsEvent } = useAnalytics();
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	const analyticsAttributes = useMemo(
		() => ({
			type: 'button',
			icon,
			appearance,
			isEducationDiscount,
		}),
		[icon, appearance, isEducationDiscount],
	);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(e, analyticsEvent);

			// Fire exposure for ea_to_osb_experiment control tenants
			if (isEligibleForEAtoOSBExperiment(context)) {
				getExperimentValue({
					statsigParams: {
						experimentName: 'ea_to_osb_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
					trackEventParameters: {
						trackEventGrowthInitiative: 'tako',
					},
				});
			}

			const updatedAnalyticsEvent = analyticsEvent.update((payload) => ({
				...payload,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editionAwareness',
				attributes: analyticsAttributes,
			}));
			sendAnalyticsEvent(updatedAnalyticsEvent);
		},
		[onClick, sendAnalyticsEvent, analyticsAttributes, getExperimentValue, context],
	);

	const handleOnMouseEnter = useCallback(() => {
		if (!fg('ea_hover_analytics_event_gate')) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'editionAwareness',
			attributes: analyticsAttributes,
		});
	}, [sendAnalyticsEvent, analyticsAttributes]);

	const Button = hasCustomTheme ? (
		// When there's a custom theme applied we just return a button which gets styled by the theme
		<TopNavButton
			{...rest}
			ref={ref}
			onClick={handleOnClick}
			onMouseEnter={handleOnMouseEnter}
			iconBefore={() => <Icon appearance={appearance} icon={icon} color="currentColor" />}
		>
			{text}
			<UiViewedEvent
				actionSubjectId="editionAwareness"
				actionSubject="button"
				attributes={analyticsAttributes}
			/>
		</TopNavButton>
	) : (
		<Pressable
			{...rest}
			ref={ref}
			xcss={[
				buttonStyles,
				appearance === 'default' && defaultStyles,
				appearance === 'warning' && warningStyles,
				appearance === 'danger' && dangerStyles,
				isDismissible && dismissibleStyles,
			]}
			onClick={handleOnClick}
			onMouseEnter={handleOnMouseEnter}
		>
			<Box xcss={iconStyles}>
				<Icon appearance={appearance} icon={icon} />
			</Box>
			{text}
			<UiViewedEvent
				actionSubjectId="editionAwareness"
				actionSubject="button"
				attributes={analyticsAttributes}
			/>
		</Pressable>
	);

	if (isDismissible) {
		return (
			<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
				<Flex alignItems="center">
					{Button}
					<DismissMenu />
				</Flex>
			</SpotlightTarget>
		);
	}

	return <SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>{Button}</SpotlightTarget>;
});

export const EditionAwarenessLink: React.ForwardRefExoticComponent<
	Omit<
		Omit<AnchorProps, 'children'> & {
			linkId: LinkId;
			touchpointIntent: TouchpointIntent;
		},
		'ref'
	> &
		React.RefAttributes<HTMLAnchorElement>
> = forwardRef<
	HTMLAnchorElement,
	Omit<AnchorProps, 'children'> & {
		linkId: LinkId;
		touchpointIntent: TouchpointIntent;
	}
>(function ({ onClick, href, touchpointIntent, linkId, ...rest }, ref) {
	const buttonContent = useButtonContent();
	if (buttonContent === null) {
		throw new Error('The button content is null, please check the context');
	}

	const { text, icon, appearance = 'default' } = buttonContent;

	const hasCustomTheme = useHasCustomTheme();

	const { sendAnalyticsEvent } = useAnalytics();
	const { origin, url: tracedUrl } = useTracedUrl(href);
	if (!tracedUrl) {
		throw new Error('The link url is undefined, please check the context');
	}
	const analyticsAttributes = useMemo(
		() => ({
			type: 'link',
			icon,
			appearance,
			touchpointIntent,
			linkId,
			...origin.toAnalyticsAttributes(),
		}),
		[icon, appearance, touchpointIntent, linkId, origin],
	);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(e, analyticsEvent);
			const updatedAnalyticsEvent = analyticsEvent.update((payload) => ({
				...payload,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editionAwareness',
				attributes: analyticsAttributes,
			}));
			sendAnalyticsEvent(updatedAnalyticsEvent);
		},
		[onClick, sendAnalyticsEvent, analyticsAttributes],
	);

	const handleOnMouseEnter = useCallback(() => {
		if (!fg('ea_hover_analytics_event_gate')) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'editionAwareness',
			attributes: analyticsAttributes,
		});
	}, [sendAnalyticsEvent, analyticsAttributes]);

	if (hasCustomTheme) {
		// When there's a custom theme applied we just return a button which gets styled by the theme
		return (
			<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
				<TopNavLinkButton
					{...rest}
					href={tracedUrl}
					ref={ref}
					onClick={handleOnClick}
					onMouseEnter={handleOnMouseEnter}
					iconBefore={() => <Icon appearance={appearance} icon={icon} color="currentColor" />}
				>
					{text}
					<UiViewedEvent
						actionSubjectId="editionAwareness"
						actionSubject="button"
						attributes={analyticsAttributes}
					/>
				</TopNavLinkButton>
			</SpotlightTarget>
		);
	}

	return (
		<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
			<Anchor
				{...rest}
				href={tracedUrl}
				ref={ref}
				xcss={[
					buttonStyles,
					appearance === 'default' && defaultStyles,
					appearance === 'warning' && warningStyles,
					appearance === 'danger' && dangerStyles,
				]}
				onClick={handleOnClick}
				onMouseEnter={handleOnMouseEnter}
			>
				<Box xcss={iconStyles}>
					<Icon appearance={appearance} icon={icon} />
				</Box>
				{text}
				<UiViewedEvent
					actionSubjectId="editionAwareness"
					actionSubject="button"
					attributes={analyticsAttributes}
				/>
			</Anchor>
		</SpotlightTarget>
	);
});

const capitalizeName = (name: string) =>
	name
		.split(' ')
		.map((word) => {
			return word[0].toUpperCase() + word.substring(1);
		})
		.join(' ');

function DismissMenu() {
	const { formatMessage } = useIntl();

	const fireDismissMenuEvent = useOnClickDismissMenu();
	const { product, edition, onDismissButton } = useEditionAwareness();
	const { setActiveSpotlight } = useSpotlight();

	const hasCustomTheme = useHasCustomTheme();

	const trialOfferingName = getTrialOfferingName(product, edition);

	const handleOnEvents = useCallback(
		(e: ACTEvent) => {
			if (e.type === 'DISMISS_SUCCEEDED' && onDismissButton) {
				onDismissButton();
				fireDismissMenuEvent('dismiss');
				setActiveSpotlight('standard-to-premium');
			}

			if (e.type === 'AD_CONTROL_DROP_DOWN_MENU_OPENED') {
				fireDismissMenuEvent('openMenu');
			}

			if (e.type === 'REASONS_BUTTON_CLICKED') {
				fireDismissMenuEvent('learnMore');
			}
		},
		[onDismissButton, setActiveSpotlight, fireDismissMenuEvent],
	);

	const dismissMenuAriaLabel = formatMessage({
		id: '@atlassian/edition-awareness.edition-awareness-dismiss-button.menu-aria',
		defaultMessage: 'More',
		description:
			'The aria label for the dismiss menu, indicating to the user that there are further actions available.',
	});

	const getAdControlsTrigger = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		({ triggerRef, ...props }: any) => {
			return hasCustomTheme ? (
				<TopNavIconButton
					{...props}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					aria-label={dismissMenuAriaLabel}
				/>
			) : (
				<Pressable
					{...props}
					ref={triggerRef}
					xcss={[dismissMenuStyles]}
					aria-label={dismissMenuAriaLabel}
				>
					<ShowMoreHorizontalIcon label="" spacing="none" color={token('color.icon.subtlest')} />
				</Pressable>
			);
		},
		[hasCustomTheme, dismissMenuAriaLabel],
	);

	const offering = (
		<Text weight="bold">
			{capitalizeName(product.replace(/-/g, ' '))} {capitalizeName(trialOfferingName)}
		</Text>
	);

	return (
		<AdControlProvider onEvents={(e) => handleOnEvents(e)} shouldShowDismissFlag={false}>
			<AdControlDropdownMenu itemId="default-item" trigger={getAdControlsTrigger}>
				{(props) => (
					<>
						<DismissalButton {...props}>
							<FormattedMessage
								id="@atlassian/edition-awareness.edition-awareness-dismiss-button.dismiss"
								defaultMessage="Dismiss"
								description="An action for the user to permanently dismiss this button to prevent it from appearing again"
							/>
						</DismissalButton>
						<ReasonsButton
							{...props}
							modalProps={{
								body: (
									<Stack space="space.150">
										<Box>
											<FormattedMessage
												id="@atlassian/edition-awareness.edition-awareness-dismiss-button.why-am-i-seeing-this"
												defaultMessage="We think {offering} will improve your team's productivity. This recommendation is only shown to billing admins and is based on:"
												description="A message that informs the user why they are seeing the current plan recommendation for their current product"
												values={{
													offering,
												}}
											/>
										</Box>

										<Stack as="ul" xcss={reasonsUlStyles}>
											<li>
												<FormattedMessage
													id="@atlassian/edition-awareness.edition-awareness-dismiss-button.reason-1"
													defaultMessage="the size of your team"
													description="A reason why the user is seeing the current plan recommendation"
												/>
											</li>
											<li>
												<FormattedMessage
													id="@atlassian/edition-awareness.edition-awareness-dismiss-button.reason-2"
													defaultMessage="your existing Atlassian products"
													description="A reason why the user is seeing the current plan recommendation"
												/>
											</li>
										</Stack>

										<Box>
											<Link
												appearance="default"
												href="https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers"
												target="_blank"
												onClick={() => fireDismissMenuEvent('clickLink')}
											>
												<FormattedMessage
													id="@atlassian/edition-awareness.edition-awareness-dismiss-button.data-privacy.link"
													defaultMessage="Learn more about your data privacy with Atlassian"
													description="Link to Atlassian data privacy page"
												/>
											</Link>
										</Box>
									</Stack>
								),
							}}
						/>
					</>
				)}
			</AdControlDropdownMenu>
		</AdControlProvider>
	);
}
